import { useSwapSlippage } from '@plug-core/state'
import { Field, Input, SegmentedControl, Sheet, Text } from '@ui'
import React, { FC, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  visible?: boolean
  close?: () => void
}

const SLIPPAGE_PRESETS = ['0.5', '1', '2'] as const
type SlippageOption = (typeof SLIPPAGE_PRESETS)[number] | 'custom'

const SwapConfig: FC<Props> = ({ visible, close }) => {
  const { slippage, setSlippage } = useSwapSlippage()
  const [slippageValue, setSlippageValue] = useState<string>(`${slippage}`)
  const [slippageError, setSlippageError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const { t } = useTranslation()

  const slippageHigh = useMemo(() => {
    return parseFloat(`${slippage}`) > 4
  }, [slippage])

  const onSlippageChange = (value: string) => {
    setSlippageValue(value)
    const parsed = parseFloat(value)

    setSlippageError(isNaN(parsed))

    if (!isNaN(parsed)) {
      setSlippage(parsed)
    }
  }

  const selectedOption: SlippageOption =
    SLIPPAGE_PRESETS.find((preset) => preset === slippageValue) ?? 'custom'

  const onOptionChange = (option: SlippageOption) => {
    if (option === 'custom') {
      inputRef.current?.focus()
      inputRef.current?.select()
      return
    }

    onSlippageChange(option)
  }

  return (
    <Sheet open={!!visible} onClose={close} dismissible={!!close} title={t('swap.config')}>
      <Field
        label={t('swap.slippage')}
        helper={slippageHigh ? `🚨 ${t('swap.errors.slippageTooHigh')}` : undefined}
        error={slippageError}
      >
        <div className="flex items-center gap-3">
          <SegmentedControl<SlippageOption>
            aria-label={t('swap.slippage')}
            value={selectedOption}
            onChange={onOptionChange}
            options={[
              ...SLIPPAGE_PRESETS.map((preset) => ({ value: preset, label: `${preset}%` })),
              { value: 'custom' as const, label: t('swap.slippageCustom') },
            ]}
            size="sm"
            className="shrink-0"
          />
          <Input
            ref={inputRef}
            inputMode="decimal"
            value={slippageValue}
            onChange={(e) => onSlippageChange(e.target.value)}
            className="text-center tabular"
            wrapperClassName="min-w-0 flex-1"
            trailing={
              <Text as="span" variant="body-s" tone="fg-2">
                %
              </Text>
            }
          />
        </div>
      </Field>
    </Sheet>
  )
}

export default SwapConfig
